import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  SafetyCertificateOutlined,
  WarningFilled,
  CheckCircleFilled,
  CloseOutlined,
  ReloadOutlined,
  ClockCircleOutlined,
  PictureOutlined,
  LeftOutlined,
  RightOutlined,
  ShopOutlined,
  ZoomInOutlined,
} from "@ant-design/icons";
import axios from "../../utils/axiosDash";
import { message, Table } from "antd";

const PAGE_SIZE = 10;
const STORE_STORAGE_KEY = "selectedStore";
const ALL_STORES = "All Stores";

/* ------------------------------ helpers -------------------------------- */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n) => String(n).padStart(2, "0");

const getId = (a) => a?._id?.$oid || a?._id || String(a?.postgresId ?? Math.random());
const getDate = (v) => new Date(v?.$date || v);
const titleCase = (s = "") => s.charAt(0).toUpperCase() + s.slice(1);

// Display stored value as-is (UTC fields, no timezone shift)
const fmtTime = (d) => {
  const h = d.getUTCHours();
  return `${pad(h % 12 === 0 ? 12 : h % 12)}:${pad(d.getUTCMinutes())} ${h >= 12 ? "PM" : "AM"}`;
};
const toInputDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const shiftDate = (str, days) => {
  const [y, m, d] = str.split("-").map(Number);
  return toInputDate(new Date(y, m - 1, d + days));
};
const prettyInputDate = (str) => {
  const [y, m, d] = str.split("-").map(Number);
  return `${pad(d)} ${MONTHS[m - 1]} ${y}`;
};

const readStore = () => {
  try {
    return localStorage.getItem(STORE_STORAGE_KEY) || ALL_STORES;
  } catch {
    return ALL_STORES;
  }
};
const isAllStores = (name) => !name || name === ALL_STORES;

function normalize(a) {
  const hasGloveData = a.gloveStatus != null && String(a.gloveStatus).trim() !== "";
  const hasHairnetData = a.hairnetStatus != null && String(a.hairnetStatus).trim() !== "";

  // Duster alerts arrive as alertType "duster" (gloves / hairnet are null on those rows).
  // The old status fields are still honoured as a fallback.
  const isDusterAlert = String(a.alertType || "").trim().toLowerCase() === "duster";
  const dusterRaw = String(a.dusterStatus ?? a.dusterDetected ?? "").toUpperCase();
  const dusterVisible =
    isDusterAlert || ["YES", "TRUE", "1", "VISIBLE", "DETECTED"].includes(dusterRaw);

  // Duster is its own kind of alert: a duster row is never also counted as gloves / hairnet.
  // A gloves / hairnet frame can have BOTH missing: it then counts in each, and in "both".
  const noGloves = !dusterVisible && String(a.gloveStatus).toUpperCase() === "NO";
  const noHairnet = !dusterVisible && String(a.hairnetStatus).toUpperCase() === "NO";

  return {
    ...a,
    id: getId(a),
    ts: getDate(a.postgresCreatedAt),
    store: titleCase(a.restaurant || "Unknown"),
    noGloves,
    noHairnet,
    both: noGloves && noHairnet,
    hasGloveData,
    hasHairnetData,
    dusterVisible,
  };
}

/* ------------------------- light colour tokens ------------------------- */
// One soft palette used everywhere: sky (primary), mint (ok), peach (hairnet),
// amber (duster), blush (alerts). Fills are pastel (100-300), text is mid-tone (600-700).
const TONE = {
  sky: { tile: "bg-sky-100 text-sky-600", bar: "bg-sky-300", dot: "bg-sky-300" },
  peach: { tile: "bg-orange-100 text-orange-500", bar: "bg-orange-200", dot: "bg-orange-200" },
  amber: { tile: "bg-amber-100 text-amber-600", bar: "bg-amber-300", dot: "bg-amber-300" },
  blush: { tile: "bg-rose-100 text-rose-500", bar: "bg-rose-300", dot: "bg-rose-300" },
  mint: { tile: "bg-emerald-100 text-emerald-600", bar: "bg-emerald-300", dot: "bg-emerald-300" },
  violet: { tile: "bg-violet-100 text-violet-500", bar: "bg-violet-300", dot: "bg-violet-300" },
};

const softBtn =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-sky-100 bg-white font-semibold text-sky-700 transition-colors hover:bg-sky-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-200";

/* ------------------------------ small UI ------------------------------- */

function Card({ className = "", children }) {
  return (
    <div
      className={`rounded-3xl border border-sky-100 bg-white shadow-[0_10px_30px_rgba(125,170,210,0.12)] ${className}`}
    >
      {children}
    </div>
  );
}

function SectionTitle({ title, hint, right }) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-base font-bold text-slate-700">{title}</h2>
        {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
      </div>
      {right}
    </div>
  );
}

function Stat({ label, value, tone, icon }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-white/80 bg-white/90 px-4 py-3.5 shadow-[0_6px_20px_rgba(125,170,210,0.14)]">
      <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-lg ${tone}`}>{icon}</div>
      <div className="min-w-0">
        <p className="truncate text-2xl font-bold leading-none tabular-nums text-slate-700">{value}</p>
        <p className="mt-1.5 truncate text-xs text-slate-400">{label}</p>
      </div>
    </div>
  );
}

function BarRow({ label, value, total, tone }) {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2 text-sm">
        <span className="truncate font-medium text-slate-600">{label}</span>
        <span className="tabular-nums text-slate-400">
          <b className="text-slate-600">{value}</b> · {pct}%
        </span>
      </div>
      <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full ${tone} transition-all duration-500`}
          style={{ width: `${value ? Math.max(pct, 4) : 0}%` }}
        />
      </div>
    </div>
  );
}

function Pill({ ok, okText, badText, na }) {
  if (na) {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-400">
        -
      </span>
    );
  }
  return ok ? (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-600">
      <CheckCircleFilled /> {okText}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-500">
      <WarningFilled /> {badText}
    </span>
  );
}

function AlertImage({ src, alt, className = "" }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className={`flex items-center justify-center bg-sky-50 text-sky-300 ${className}`}>
        <PictureOutlined className="text-xl" />
      </div>
    );
  }
  return <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} className={`object-cover ${className}`} />;
}

/* ------------------------------ lightbox ------------------------------- */

function Lightbox({ items, index, onClose, onNav }) {
  const item = items[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onNav(-1);
      if (e.key === "ArrowRight") onNav(1);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, onNav]);

  if (!item) return null;
  const navBtn =
    "absolute top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-sky-600 shadow-md ring-1 ring-sky-100 hover:bg-sky-50";

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-sky-100/80 p-3 backdrop-blur-md sm:p-8"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute right-3 top-3 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white text-lg text-sky-600 shadow-md ring-1 ring-sky-100 hover:bg-sky-50 sm:right-6 sm:top-6"
      >
        <CloseOutlined />
      </button>
      {index > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNav(-1);
          }}
          aria-label="Previous image"
          className={`${navBtn} left-2 sm:left-6`}
        >
          <LeftOutlined />
        </button>
      )}
      {index < items.length - 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNav(1);
          }}
          aria-label="Next image"
          className={`${navBtn} right-2 sm:right-6`}
        >
          <RightOutlined />
        </button>
      )}
      <img
        src={item.imagePath}
        alt="Alert snapshot"
        onClick={(e) => e.stopPropagation()}
        className="max-h-full max-w-full rounded-2xl object-contain shadow-xl ring-4 ring-white"
      />
    </div>
  );
}

/* ------------------------------ main page ------------------------------ */

export default function SafetyHygieneCompliance() {
  const [storeName, setStoreName] = useState(readStore);
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(toInputDate(new Date()));
  const [issueFilter, setIssueFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [viewIndex, setViewIndex] = useState(null);

  // Used to discard responses from requests that are no longer current
  // (e.g. user quickly switches stores while a request is in flight).
  const requestId = useRef(0);

  const today = toInputDate(new Date());
  const showRestaurant = isAllStores(storeName);

  /* --- Keep storeName in sync with the global store selector --- */
  useEffect(() => {
    const sync = () => {
      const next = readStore();
      setStoreName((prev) => (prev === next ? prev : next));
    };
    const onStorage = (e) => {
      if (e.key === STORE_STORAGE_KEY) sync();
    };
    // Same-tab store changes: assume the global selector dispatches this event.
    // If it doesn't, switch to polling or lift store into a shared context.
    const onCustom = () => sync();

    window.addEventListener("storage", onStorage);
    window.addEventListener("storeChanged", onCustom);
    window.addEventListener("focus", sync);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("storeChanged", onCustom);
      window.removeEventListener("focus", sync);
    };
  }, []);

  /* --- Fetch whenever the store OR the date changes --- */
  const fetchAlerts = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const res = await axios.post("/get-empire-hygiene-safety-alerts", {
        store: storeName,
        date,
      });
      if (id !== requestId.current) return; // a newer request superseded this one
      if (res.data?.success) {
        const d = res.data.data;
        setRaw(Array.isArray(d) ? d : d?.alerts || d?.violations || []);
      } else {
        setRaw([]);
      }
    } catch (err) {
      if (id !== requestId.current) return;
      console.error("Error fetching hygiene alerts:", err);
      setRaw([]);
      message.error(err.response?.data?.message || "Failed to fetch hygiene and safety data");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [storeName, date]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const changeDate = (next) => {
    if (!next) return;
    setDate(next);
    setPage(1);
    setViewIndex(null);
  };

  // Reset paging + lightbox whenever scope changes
  useEffect(() => {
    setPage(1);
    setViewIndex(null);
  }, [storeName, date]);

  /* --- Derive everything from the freshly fetched raw --- */
  const alerts = useMemo(
    () =>
      raw
        .map(normalize)
        .filter((a) => a.noGloves || a.noHairnet || a.dusterVisible)
        .sort((a, b) => b.ts - a.ts),
    [raw]
  );

  const filtered = useMemo(
    () =>
      alerts.filter((a) => {
        switch (issueFilter) {
          case "Gloves":
            return a.noGloves;
          case "Hairnet":
            return a.noHairnet;
          case "Both":
            return a.both;
          case "Duster":
            return a.dusterVisible;
          default:
            return true;
        }
      }),
    [alerts, issueFilter]
  );

  const stats = useMemo(
    () => ({
      total: alerts.length,
      gloves: alerts.filter((a) => a.noGloves).length,
      hairnet: alerts.filter((a) => a.noHairnet).length,
      both: alerts.filter((a) => a.both).length,
      duster: alerts.filter((a) => a.dusterVisible).length,
    }),
    [alerts]
  );

  const trend = useMemo(() => {
    const buckets = new Map();
    alerts.forEach((a) => {
      const h = a.ts.getUTCHours();
      if (!buckets.has(h)) buckets.set(h, { gloves: 0, hairnet: 0, duster: 0 });
      const b = buckets.get(h);
      if (a.noGloves) b.gloves += 1;
      if (a.noHairnet) b.hairnet += 1;
      if (a.dusterVisible) b.duster += 1;
    });
    return Array.from(buckets.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([h, v]) => ({ label: `${pad(h)}:00`, ...v }));
  }, [alerts]);
  const trendTotal = (t) => t.gloves + t.hairnet + t.duster;
  const trendMax = Math.max(1, ...trend.map(trendTotal));
  const peak = trend.reduce((best, t) => (!best || trendTotal(t) > trendTotal(best) ? t : best), null);

  const byStore = useMemo(() => {
    const m = new Map();
    alerts.forEach((a) => {
      const s = m.get(a.store) || { store: a.store, violations: 0 };
      s.violations += 1;
      m.set(a.store, s);
    });
    return Array.from(m.values()).sort((a, b) => b.violations - a.violations);
  }, [alerts]);

  const FILTERS = [
    ["All", "All"],
    ["Gloves", "No gloves"],
    ["Hairnet", "No hairnet"],
    ["Both", "Both missing"],
    ["Duster", "Duster Detected"],
  ];

  const navLightbox = useCallback(
    (dir) => setViewIndex((i) => (i === null ? i : Math.min(Math.max(i + dir, 0), filtered.length - 1))),
    [filtered.length]
  );

  /* ------------------------------ table columns ------------------------------ */

  const columns = useMemo(() => {
    const cols = [
      {
        title: "Snapshot",
        key: "image",
        width: 120,
        render: (_, a) => {
          const idx = filtered.findIndex((x) => x.id === a.id);
          return (
            <button
              type="button"
              onClick={() => setViewIndex(idx)}
              aria-label="View snapshot"
              className="group relative block overflow-hidden rounded-xl ring-1 ring-sky-100"
            >
              <AlertImage src={a.imagePath} alt={`${a.store} snapshot`} className="h-14 w-24" />
              <span className="absolute inset-0 flex items-center justify-center bg-sky-100/70 text-sky-700 opacity-0 transition-opacity group-hover:opacity-100">
                <ZoomInOutlined />
              </span>
            </button>
          );
        },
      },
      {
        title: "Status",
        key: "status",
        width: 300,
        render: (_, a) => (
          <span className="whitespace-nowrap text-sm font-semibold text-rose-400">
            {[a.noGloves && "No gloves", a.noHairnet && "No hairnet", a.dusterVisible && "Duster Detected"]
              .filter(Boolean)
              .join(" + ")}
          </span>
        ),
      },
      {
        title: "Gloves",
        key: "gloves",
        width: 130,
        render: (_, a) => <Pill na={!a.hasGloveData} ok={!a.noGloves} okText="Worn" badText="Missing" />,
      },
      {
        title: "Hairnet",
        key: "hairnet",
        width: 130,
        render: (_, a) => <Pill na={!a.hasHairnetData} ok={!a.noHairnet} okText="Worn" badText="Missing" />,
      },
      {
        title: "Duster",
        key: "duster",
        width: 130,
        render: (_, a) => <Pill ok={!a.dusterVisible} okText="Not seen" badText="Detected" />,
      },
    ];

    // Only when aggregating across stores
    if (showRestaurant) {
      cols.push({
        title: "Store",
        dataIndex: "store",
        key: "store",
        width: 160,
        sorter: (a, b) => a.store.localeCompare(b.store),
        render: (v) => (
          <span className="flex items-center gap-2 whitespace-nowrap font-medium text-slate-600">
            <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-sky-50 text-xs text-sky-500">
              <ShopOutlined />
            </span>
            {v}
          </span>
        ),
      });
    }

    cols.push({
      title: "Time",
      key: "time",
      width: 130,
      defaultSortOrder: "descend",
      sorter: (a, b) => a.ts - b.ts,
      render: (_, a) => (
        <span className="flex items-center gap-1.5 whitespace-nowrap text-slate-500">
          <ClockCircleOutlined className="text-sky-300" /> {fmtTime(a.ts)}
        </span>
      ),
    });

    return cols;
  }, [filtered, showRestaurant]);

  return (
    <div className="bg-white pb-8 pt-6">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .hs-root, .hs-root .ant-table, .hs-root .ant-pagination { font-family: 'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif; }

        /* Table: pastel header, soft rows */
        .hs-root .ant-table { background: transparent; color: #475569; }
        .hs-root .ant-table-thead > tr > th { background: #f0f9ff; font-size: 12px; font-weight: 600; color: #64a3c9; border-bottom: 1px solid #e0f2fe; }
        .hs-root .ant-table-thead > tr > th::before { display: none !important; }
        .hs-root .ant-table-tbody > tr > td { border-bottom: 1px solid #f1f5f9; }
        .hs-root .ant-table-tbody > tr:hover > td { background: #f8fcff !important; }
        .hs-root .ant-table-column-sorter { color: #bae6fd; }
        .hs-root .ant-table-column-sorter-up.active, .hs-root .ant-table-column-sorter-down.active { color: #38bdf8; }

        /* Pagination: light active state instead of solid blue */
        .hs-root .ant-pagination-item { border-radius: 10px; border-color: #e0f2fe; }
        .hs-root .ant-pagination-item a { color: #64748b; }
        .hs-root .ant-pagination-item:hover { border-color: #bae6fd; background: #f0f9ff; }
        .hs-root .ant-pagination-item-active { background: #e0f2fe; border-color: #bae6fd; }
        .hs-root .ant-pagination-item-active a { color: #0369a1; font-weight: 700; }
        .hs-root .ant-pagination-prev .ant-pagination-item-link,
        .hs-root .ant-pagination-next .ant-pagination-item-link { border-radius: 10px; color: #64748b; }
        .hs-root .ant-pagination-total-text { color: #94a3b8; font-size: 12px; }

        /* Loading spinner */
        .hs-root .ant-spin-dot-item { background-color: #7dd3fc; }
        .hs-root .ant-spin-blur { opacity: 0.5; }

        @media (prefers-reduced-motion: reduce) { .hs-root * { transition: none !important; } }
      `}</style>

      {/* Hero */}
      <div className="bg-white pb-8 pt-6">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8">
          <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-xl text-sky-500 shadow-[0_6px_20px_rgba(125,170,210,0.25)] ring-1 ring-sky-100">
                <SafetyCertificateOutlined />
              </div>
              <div>
                <h1 className="text-xl font-extrabold leading-tight text-slate-700 sm:text-2xl">
                  Safety &amp; Hygiene Violations
                </h1>
                <p className="mt-0.5 text-xs text-slate-500 sm:text-sm">
                  {showRestaurant ? "All Stores" : storeName} · Gloves, hairnet and duster monitoring for{" "}
                  {prettyInputDate(date)}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center rounded-xl border border-sky-100 bg-white shadow-sm">
                <button
                  onClick={() => changeDate(shiftDate(date, -1))}
                  aria-label="Previous day"
                  className="flex h-10 w-10 items-center justify-center rounded-l-xl text-sky-500 hover:bg-sky-50"
                >
                  <LeftOutlined className="text-xs" />
                </button>
                <input
                  type="date"
                  value={date}
                  max={today}
                  onChange={(e) => changeDate(e.target.value)}
                  aria-label="Select date"
                  className="h-10 border-x border-sky-100 bg-white px-2 text-sm font-medium text-slate-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sky-200"
                />
                <button
                  onClick={() => changeDate(shiftDate(date, 1))}
                  disabled={date >= today}
                  aria-label="Next day"
                  className="flex h-10 w-10 items-center justify-center rounded-r-xl text-sky-500 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <RightOutlined className="text-xs" />
                </button>
              </div>
              {date !== today && (
                <button onClick={() => changeDate(today)} className={`${softBtn} h-10 px-4 text-sm`}>
                  Today
                </button>
              )}
              <button
                onClick={fetchAlerts}
                className="inline-flex h-10 items-center gap-2 rounded-xl bg-sky-100 px-4 text-sm font-bold text-sky-700 transition-colors hover:bg-sky-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
              >
                <ReloadOutlined spin={loading} /> Refresh
              </button>
            </div>
          </header>

          <div className="mt-8 grid w-full grid-cols-2 gap-3 lg:grid-cols-5">
            <Stat label="Total violations" value={stats.total} tone={TONE.blush.tile} icon={<WarningFilled />} />
            <Stat label="No gloves" value={stats.gloves} tone={TONE.sky.tile} icon={<SafetyCertificateOutlined />} />
            <Stat label="No hairnet" value={stats.hairnet} tone={TONE.peach.tile} icon={<SafetyCertificateOutlined />} />
            <Stat label="Duster Detected" value={stats.duster} tone={TONE.amber.tile} icon={<SafetyCertificateOutlined />} />
            {showRestaurant ? (
              <Stat label="Stores reporting" value={byStore.length} tone={TONE.mint.tile} icon={<ShopOutlined />} />
            ) : (
              <Stat label="Store" value={storeName} tone={TONE.mint.tile} icon={<ShopOutlined />} />
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto mt-5 max-w-[1440px] px-4 pb-10 sm:px-6 lg:px-8">
        {/* Analytics */}
        <section className="mb-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
          <Card className="p-5 lg:col-span-2">
            <SectionTitle
              title="Violations by Hour"
              hint={peak ? `Busiest Hour: ${peak.label} (${trendTotal(peak)} violations)` : prettyInputDate(date)}
              right={
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <span className={`h-2.5 w-2.5 rounded-full ${TONE.sky.dot}`} /> Gloves
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className={`h-2.5 w-2.5 rounded-full ${TONE.peach.dot}`} /> Hairnet
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className={`h-2.5 w-2.5 rounded-full ${TONE.amber.dot}`} /> Duster
                  </span>
                </div>
              }
            />
            {trend.length ? (
              <div className="overflow-x-auto">
                <div className="flex h-44 min-w-[320px] items-end gap-2 border-b border-sky-100 pb-1">
                  {trend.map((t) => {
                    const total = trendTotal(t);
                    return (
                      <div
                        key={t.label}
                        className="flex h-full flex-1 flex-col items-center justify-end"
                        title={`${t.label}: ${t.gloves} gloves, ${t.hairnet} hairnet, ${t.duster} duster Detected`}
                      >
                        <span className="mb-1 text-[11px] font-semibold tabular-nums text-slate-400">{total}</span>
                        <div
                          className="flex w-full max-w-[40px] flex-col justify-end overflow-hidden rounded-t-lg"
                          style={{ height: `${(total / trendMax) * 85}%`, minHeight: total ? 4 : 0 }}
                        >
                          <div className={TONE.amber.bar} style={{ flex: t.duster }} />
                          <div className={TONE.peach.bar} style={{ flex: t.hairnet }} />
                          <div className={TONE.sky.bar} style={{ flex: t.gloves }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 flex min-w-[320px] gap-2">
                  {trend.map((t) => (
                    <span key={t.label} className="flex-1 text-center text-[10px] text-slate-400">
                      {t.label}
                    </span>
                  ))}
                </div>
              </div>
            ) : (
              <p className="py-12 text-center text-sm text-slate-400">No violations recorded on this date.</p>
            )}
          </Card>

          <Card className="p-5">
            <SectionTitle title="What's Being Flagged" hint="Share of all violations" />
            <div className="space-y-5">
              <BarRow label="Missing Gloves" value={stats.gloves} total={stats.total} tone={TONE.sky.bar} />
              <BarRow label="Missing Hairnet" value={stats.hairnet} total={stats.total} tone={TONE.peach.bar} />
              <BarRow label="Both Missing" value={stats.both} total={stats.total} tone={TONE.blush.bar} />
              <BarRow label="Duster Detected" value={stats.duster} total={stats.total} tone={TONE.amber.bar} />
            </div>
          </Card>
        </section>

        {/* Store performance — only when viewing all stores */}
        {showRestaurant && byStore.length > 1 && (
          <Card className="mb-5 p-5">
            <SectionTitle title="Store Performance" hint="Share of all violations, highest first" />
            <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
              {byStore.map((s) => (
                <BarRow key={s.store} label={s.store} value={s.violations} total={stats.total} tone={TONE.violet.bar} />
              ))}
            </div>
          </Card>
        )}

        {/* Alerts table */}
        <Card className="overflow-hidden p-4 sm:p-5">
          <SectionTitle
            title="Alert Log"
            hint={`${filtered.length} of ${stats.total} violations · click a snapshot to enlarge`}
            right={
              <div className="flex max-w-full gap-1 overflow-x-auto rounded-xl bg-sky-50 p-1">
                {FILTERS.map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => {
                      setIssueFilter(key);
                      setPage(1);
                    }}
                    className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-200 ${
                      issueFilter === key
                        ? "bg-white text-sky-700 shadow-sm ring-1 ring-sky-100"
                        : "text-slate-500 hover:bg-white/70 hover:text-sky-600"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            }
          />
          <Table
            rowKey="id"
            columns={columns}
            dataSource={filtered}
            loading={loading}
            scroll={{ x: showRestaurant ? 950 : 790 }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              showSizeChanger: false,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              onChange: (p) => setPage(p),
            }}
            locale={{
              emptyText: (
                <div className="py-10 text-center">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-3xl text-emerald-300">
                    <CheckCircleFilled />
                  </span>
                  <p className="mt-3 text-sm font-semibold text-slate-600">
                    No violations on {prettyInputDate(date)}
                    {!showRestaurant && ` at ${storeName}`}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">Try a different date or filter.</p>
                </div>
              ),
            }}
          />
        </Card>
      </div>

      {viewIndex !== null && (
        <Lightbox items={filtered} index={viewIndex} onClose={() => setViewIndex(null)} onNav={navLightbox} />
      )}
    </div>
  );
}